import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import type { Replayer, eventWithTime } from 'rrweb';
import 'rrweb/dist/style.css';
import { ApiError, api, type SessionRecordingView } from '../api/client';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { fullWhen } from '../lib/format';
import { StorageChip } from '../pages/tenant/RecordingSettings';
import { Button, Loading, Notice } from './ui';

const SPEEDS = [1, 2, 4, 8];

function clock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** "/t/acme.example/people/201" → "people/201". */
export function pageName(path: string): string {
  const m = /^\/t\/[^/]+\/?(.*)$/.exec(path);
  return m ? m[1] || 'home' : path;
}

/** Where a replay opens: its own tab, loaded fresh (the page copy needs its own style rules). */
export function replayUrl(domain: string, id: string): string {
  return `/replay/${encodeURIComponent(domain)}/${encodeURIComponent(id)}`;
}

/**
 * Watching one session recording, in its own tab: the portal as that person saw it, with play,
 * seek and speed. Company admins only (the API checks).
 */
export function SessionReplayPage() {
  const { state } = useAuth();
  const { tenant = '', id = '' } = useParams();
  const area = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const player = useRef<Replayer | null>(null);
  const [rec, setRec] = useState<SessionRecordingView | null>(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [total, setTotal] = useState(0);
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [skip, setSkip] = useState(true);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1);
  const signedIn = Boolean(state?.signedIn);

  useEffect(() => {
    if (!signedIn) return;
    let gone = false;
    void (async () => {
      try {
        const [r, rrweb] = await Promise.all([api.screenRecordingEvents(tenant, id), import('rrweb')]);
        if (gone || !stage.current) return;
        setRec(r.recording);
        document.title = `${r.recording.userName}’s session · Stitch`;
        if (r.events.length < 2) {
          setError('Nothing was recorded in this session (the page was closed straight away).');
          return;
        }
        const p = new rrweb.Replayer(r.events as eventWithTime[], {
          root: stage.current,
          skipInactive: true,
          showWarning: false,
          triggerFocus: false,
          mouseTail: false,
          UNSAFE_replayCanvas: false,
          insertStyleRules: ['html, body { overflow: hidden !important; }']
        });
        p.on('resize', (d) => {
          const { width, height } = d as { width: number; height: number };
          setSize({ w: width, h: height });
        });
        p.on('finish', () => setPlaying(false));
        player.current = p;
        setTotal(p.getMetaData().totalTime);
        p.pause(0);
        setReady(true);
      } catch (err) {
        if (gone) return;
        setError(err instanceof ApiError && err.status === 404 ? 'That recording isn’t here. It may have been deleted, or its time was up.' : err instanceof Error ? err.message : 'The recording couldn’t be loaded.');
      }
    })();
    return () => {
      gone = true;
      player.current?.destroy();
      player.current = null;
    };
  }, [signedIn, tenant, id]);

  // Where the replay is, while it plays.
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => setAt(player.current?.getCurrentTime() ?? 0), 250);
    return () => window.clearInterval(t);
  }, [playing]);

  // Their window, fitted into this one.
  useEffect(() => {
    const fit = () => {
      const box = area.current?.getBoundingClientRect();
      if (!box || !size) return;
      setScale(Math.min(1, (box.width - 24) / size.w, (box.height - 24) / size.h));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [size, ready]);

  if (!state) return <Loading label="Loading" />;
  if (!signedIn) return <Navigate to="/sign-in" replace />;

  const play = () => {
    const p = player.current;
    if (!p) return;
    p.play(at >= total - 100 ? 0 : at);
    setPlaying(true);
  };
  const pause = () => {
    player.current?.pause();
    setAt(player.current?.getCurrentTime() ?? at);
    setPlaying(false);
  };
  const seek = (ms: number) => {
    setAt(ms);
    if (playing) player.current?.play(ms);
    else player.current?.pause(ms);
  };

  return (
    <div className="replay-page">
      <header className="replay-head">
        <span className="cell-stack grow">
          <h1>{rec ? `${rec.userName}’s session` : 'Session recording'}</h1>
          {rec && (
            <span className="replay-sub muted">
              {fullWhen(rec.startedAt)} · {clock(Date.parse(rec.lastAt) - Date.parse(rec.startedAt))}
              {rec.where.map((w) => (
                <StorageChip key={w} place={w} {...(w === 'theirs' && rec.bucket ? { bucket: rec.bucket } : {})} />
              ))}
            </span>
          )}
        </span>
        <Link className="link-btn" to={`/t/${encodeURIComponent(tenant)}/activity?tab=recordings`}>
          Back to Activity
        </Link>
      </header>

      <div className="replay-area" ref={area}>
        {error ? <Notice tone="warn">{error}</Notice> : !ready && <Loading label="Loading the recording" />}
        <div className="replay-screen" style={size ? { width: size.w * scale, height: size.h * scale } : { visibility: 'hidden' }}>
          <div className="replay-stage" ref={stage} style={{ transform: `scale(${scale})` }} />
        </div>
      </div>

      {ready && (
        <div className="replay-controls">
          <Button variant="primary" icon={playing ? 'pause' : 'play'} onClick={playing ? pause : play}>
            {playing ? 'Pause' : 'Play'}
          </Button>
          <input
            className="replay-seek"
            type="range"
            min={0}
            max={Math.max(1, total)}
            step={250}
            value={Math.min(at, total)}
            aria-label="Position in the recording"
            aria-valuetext={`${clock(at)} of ${clock(total)}`}
            onChange={(e) => seek(Number(e.target.value))}
          />
          <span className="replay-time num">
            {clock(at)} / {clock(total)}
          </span>
          <div className="replay-speeds" role="group" aria-label="Speed">
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                className={`preset${s === speed ? ' on' : ''}`}
                aria-pressed={s === speed}
                onClick={() => {
                  setSpeed(s);
                  player.current?.setConfig({ speed: s });
                }}
              >
                {s}×
              </button>
            ))}
          </div>
          <label className="check replay-skip">
            <input
              type="checkbox"
              checked={skip}
              onChange={(e) => {
                setSkip(e.target.checked);
                player.current?.setConfig({ skipInactive: e.target.checked });
              }}
            />
            Skip idle time
          </label>
        </div>
      )}
      <footer className="replay-foot">
        {rec && rec.pages.length > 0 && (
          <div className="replay-pages">
            <Icon name="list" size={14} />
            <span className="muted">Pages:</span>
            {rec.pages.slice(0, 12).map((p) => (
              <code key={p}>{pageName(p)}</code>
            ))}
            {rec.pages.length > 12 && <span className="muted">and {rec.pages.length - 12} more</span>}
          </div>
        )}
        <p className="muted small">What they typed is hidden, and sound isn’t recorded. Watching this is noted under Activity.</p>
      </footer>
    </div>
  );
}
